import { AlertTitle, Box, Button, Collapse, IconButton, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import React, { useState, useEffect } from 'react';
import platform from 'platform';
import { HideBaseAuthorsInstructions } from '../../services/RegistrationService';
import { GetSessionData } from '../../services/AuthService';

interface LoginInformationAlertProps {
    onHideInstructions?: () => void;
    isBtnInside?: boolean;
    sx?: any;
}

export default function LoginInformationAlert(props: LoginInformationAlertProps) {
    const [showInstructions, setShowInstructions] = useState<boolean>(false);
    const [showBookmarkInstructions, setShowBookmarkInstructions] = useState<boolean>(false);
    const [commandButton, setCommandButton] = useState<string>('Ctrl');
    const [copySuccess, setCopySuccess] = useState<boolean>(false);
    const [sha_key, setShaKey] = useState<string>('');

    const STORAGE_KEY = 'scholar_login_instructions_hidden';

    useEffect(() => {
        const fetchShaKey = async () => {
            try {
                const sessionData = await GetSessionData();
                if (sessionData && sessionData.sha_key) {
                    setShaKey(sessionData.sha_key);
                }
            } catch (error) {
                console.error("Error fetching SHA key:", error);
            }
        };

        fetchShaKey();

        // Check if instructions should be hidden based on localStorage
        const hideInstructions = localStorage.getItem(STORAGE_KEY) === 'true';
        setShowInstructions(!hideInstructions);

        // Set platform-specific command key
        if (platform.os?.family === 'OS X') {
            setCommandButton('Command (⌘)');
        }
    }, []);

    const hideInstructionsClick = () => {
        localStorage.setItem(STORAGE_KEY, 'true');

        setShowInstructions(false);

        if (props.onHideInstructions) {
            props.onHideInstructions();
        } else {
            const req = HideBaseAuthorsInstructions();
            req.then(() => { });
        }
    };

    const bookmarkLink = () => {
        localStorage.setItem('savedUrl', window.location.pathname);

        history.pushState({}, null, '/login/' + sha_key);
        setShowBookmarkInstructions(true);
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText('https://www.scholar-inbox.com/login/' + sha_key);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
    };

    // Don't show anything if we don't have a key yet
    if (!sha_key) {
        return null;
    }

    return (
        <Box sx={{ width: '100%', ...props.sx }}>
            <Collapse in={showInstructions} sx={{ width: '100%' }}>
                <Alert
                    severity="info"
                    icon={false}
                    sx={{ 
                        borderRadius: '4px', paddingLeft: 2, paddingRight: 2, paddingTop: 1, paddingBottom: 1,
                        '& .MuiAlertTitle-root': { fontSize: '1.25rem', fontWeight: 600, mb: 2 },
                        '& .MuiAlert-message': { width: '100%' }
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: "5px", width: '100%' }}>
                        <Typography variant="body1" sx={{ alignSelf: 'flex-start' }}>
                            Here is your personal login link:
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'rgba(85, 183, 118, 0.12)', borderRadius: '8px', p: 2, width: '100%', border: '1px solid rgba(85, 183, 118, 0.2)', position: 'relative' }} >
                            <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.85rem', sm: '1rem' }, wordBreak: 'break-all', pr: { xs: 1, sm: 2 }, width: '100%', textAlign: 'left' }} >
                                https://www.scholar-inbox.com/login/{sha_key}
                            </Typography>

                            <Box sx={{ position: 'relative', flexShrink: 0 }} >
                                <IconButton
                                    onClick={handleCopyLink}
                                    size="small"
                                    color={copySuccess ? "success" : "default"}
                                    sx={{
                                        bgcolor: 'white',
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                                        '&:hover': { bgcolor: '#f5f5f5' }
                                    }}
                                    title="Copy to clipboard"
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                                {copySuccess && (
                                    <Box sx={{ position: 'absolute', top: '100%', right: 0, mt: 0.5, fontSize: '0.75rem', color: 'success.main', fontWeight: 600 }} >
                                        Copied!
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: { xs: 1.5, sm: 2 }, bgcolor: 'rgba(25, 118, 210, 0.05)', borderRadius: '8px', p: 2, width: '100%', border: '1px solid rgba(25, 118, 210, 0.15)' }} >
                            <Typography sx={{ fontWeight: 600, textAlign: 'left' }}>
                                Please bookmark this link for future access:
                            </Typography>

                            <Button
                                variant="contained"
                                startIcon={<BookmarkAddIcon />}
                                onClick={bookmarkLink}
                                size="small"
                                sx={{ 
                                    whiteSpace: 'nowrap', width: '155px', minWidth: '155px', color: '#000000', alignSelf: { xs: 'flex-start', sm: 'center' },
                                    '&:hover': { color: '#ffffff' }
                                }}
                            >
                                Bookmark Now
                            </Button>
                        </Box>

                        {showBookmarkInstructions && (
                            <Box sx={{ p: 2, bgcolor: '#fffde7', borderRadius: '8px', border: '1px solid #fff59d', width: '100%', textAlign: 'left', animation: 'pulse 2s infinite' }} >
                                <Typography sx={{ fontWeight: 600 }}>
                                    Press <strong>{commandButton} + D</strong> to add this page to your bookmarks
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    {props.isBtnInside && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                                color='secondary'
                                variant='contained'
                                sx={{
                                    backgroundColor: '#55b776', px: 3,
                                }}
                                onClick={hideInstructionsClick}
                            >
                                I've Saved My Login
                            </Button>
                        </Box>
                    )}
                </Alert>
                {!props.isBtnInside && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            color='secondary'
                            variant='contained'
                            sx={{
                                backgroundColor: '#55b776', px: 3,
                            }}
                            onClick={hideInstructionsClick}
                        >
                            I've Saved My Login
                        </Button>
                    </Box>
                )}
            </Collapse>
        </Box>
    );
}